'use client';

import { Suspense } from 'react';
import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Box, Container, Typography, Grid, Button, Card, CardContent, CardActions, Chip, Table, TableBody, TableCell, TableHead, TableRow, Paper, TableContainer, Alert, Link as MuiLink } from '@mui/material';
import CheckCircleOutlineIcon from '@/letsgo/icons/CheckCircleOutline';
import { ProCheckoutButton } from '@/components/billing/ProCheckoutButton';
import { SUPPORT_EMAIL } from '@/lib/site-contact';
import { marketingPrimaryCtaSx } from '@/lib/marketingButtonStyles';
import ExpandLessIcon from '@/letsgo/icons/ExpandLess';
import { CancelOutlined, ExpandMore } from '@/lib/mui-optimized-imports';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useState } from 'react';
import { 
  People, 
  AnalyticsOutlined, 
  ScheduleOutlined, 
  SchoolOutlined, 
  StorefrontOutlined, 
  SupportAgentOutlined, 
  StarsOutlined, 
  BusinessOutlined, 
  IntegrationInstructionsOutlined, 
  VerifiedUserOutlined, 
  ApiOutlined 
} from '@/lib/mui-optimized-imports';

const PLANS = [
  {
    title: 'Free',
    price: '$0/month',
    annualPrice: '$0/year',
    features: [
      '3 social accounts',
      'Basic analytics',
      'Community access',
      '2 free Academy courses',
      'Basic template marketplace access',
    ],
    color: 'primary',
    cta: 'Start for Free',
  },
  {
    title: 'Creator',
    price: '$19/month',
    annualPrice: '$189/year',
    features: [
      '3 social accounts',
      'Advanced analytics',
      'Basic scheduling',
      '5 Academy courses',
      'Full template marketplace access',
      'Email support',
      'Metered API networks (e.g. X publish)',
    ],
    color: 'info',
    cta: 'Upgrade to Creator',
  },
  {
    title: 'Pro',
    price: '$49/month',
    annualPrice: '$489/year',
    features: [
      'All social accounts',
      'Advanced analytics & insights',
      'Advanced scheduling',
      'All Academy courses',
      'Premium template marketplace',
      'Early access to new features',
      'Priority support',
    ],
    color: 'secondary',
    cta: 'Start Pro free trial',
    highlight: true,
  },
  {
    title: 'Enterprise',
    price: '$199/month',
    annualPrice: '$1999/year',
    features: [
      'Unlimited everything',
      'White-label options',
      'Custom integrations',
      'All Academy courses + custom training',
      'Enterprise template marketplace',
      'Dedicated support',
      'SLA guarantees',
    ],
    color: 'success',
    cta: 'Contact Sales',
  },
  {
    title: 'Standard White-Label',
    price: '$499/month',
    annualPrice: '$4999/year',
    features: [
      'Advanced white-label features',
      'Custom domain support',
      'Dedicated infrastructure',
      'Priority support',
      'Custom branding',
    ],
    color: 'warning',
    cta: 'Contact Sales',
    recommended: true,
  },
  {
    title: 'Enterprise White-Label',
    price: 'Contact Us',
    annualPrice: 'Contact Us',
    features: [
      'Unlimited white-label clients',
      'API access',
      'Advanced security',
      'Dedicated engineering support',
      'All Standard White-Label features',
    ],
    color: 'error',
    cta: 'Contact Sales',
  },
];

const FEATURES_COMPARISON = [
  { name: 'Social Accounts', free: '3', creator: '3', pro: 'All', enterprise: 'Unlimited', whiteLabelStandard: 'Unlimited', whiteLabelEnterprise: 'Unlimited' },
  { name: 'Analytics', free: 'Basic', creator: 'Advanced', pro: 'Advanced & Insights', enterprise: 'Custom', whiteLabelStandard: 'Advanced', whiteLabelEnterprise: 'Custom' },
  { name: 'Scheduling', free: 'No', creator: 'Basic', pro: 'Advanced', enterprise: 'Advanced', whiteLabelStandard: 'Advanced', whiteLabelEnterprise: 'Advanced' },
  { name: 'Metered API publish (X, etc.)', free: <CancelOutlined color="error" />, creator: <CheckCircleOutlineIcon color="primary" />, pro: <CheckCircleOutlineIcon color="primary" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CheckCircleOutlineIcon color="primary" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
  { name: 'Academy Courses', free: '2 free', creator: '5', pro: 'All', enterprise: 'All + Custom', whiteLabelStandard: 'All', whiteLabelEnterprise: 'All + Custom' },
  { name: 'Template Marketplace', free: 'Basic', creator: 'Full', pro: 'Premium', enterprise: 'Enterprise', whiteLabelStandard: 'Enterprise', whiteLabelEnterprise: 'Enterprise' },
  { name: 'Support', free: 'Community', creator: 'Email', pro: 'Priority Email & Chat', enterprise: 'Dedicated', whiteLabelStandard: 'Priority', whiteLabelEnterprise: 'Dedicated Engineering' },
  { name: 'Early Access to Features', free: <CancelOutlined color="error" />, creator: <CancelOutlined color="error" />, pro: <CheckCircleOutlineIcon color="primary" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CheckCircleOutlineIcon color="primary" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
  { name: 'White-Label Options', free: <CancelOutlined color="error" />, creator: <CancelOutlined color="error" />, pro: <CancelOutlined color="error" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CheckCircleOutlineIcon color="primary" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
  { name: 'Custom Integrations', free: <CancelOutlined color="error" />, creator: <CancelOutlined color="error" />, pro: <CancelOutlined color="error" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CancelOutlined color="error" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
  { name: 'SLA Guarantees', free: <CancelOutlined color="error" />, creator: <CancelOutlined color="error" />, pro: <CancelOutlined color="error" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CheckCircleOutlineIcon color="primary" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
  { name: 'API Access', free: <CancelOutlined color="error" />, creator: <CancelOutlined color="error" />, pro: <CancelOutlined color="error" />, enterprise: <CheckCircleOutlineIcon color="primary" />, whiteLabelStandard: <CancelOutlined color="error" />, whiteLabelEnterprise: <CheckCircleOutlineIcon color="primary" /> },
];

const PLAN_TO_FEATURE_KEY: Record<string, keyof (typeof FEATURES_COMPARISON)[0]> = {
  'Free': 'free',
  'Creator': 'creator',
  'Pro': 'pro',
  'Enterprise': 'enterprise',
  'Standard White-Label': 'whiteLabelStandard',
  'Enterprise White-Label': 'whiteLabelEnterprise',
};

const FEATURES_VISIBLE_COLLAPSED = 4;

function PricingPageInner() {
  const searchParams = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});
  const [dismissCancelledBanner, setDismissCancelledBanner] = useState(false);
  const checkoutCancelled =
    !dismissCancelledBanner && searchParams?.get('checkout') === 'cancelled';

  const handlePricingToggle = (event: React.MouseEvent<HTMLElement>, newIsAnnual: boolean) => {
    if (newIsAnnual !== null) {
      setIsAnnual(newIsAnnual);
    }
  };

  const togglePlanExpanded = (planTitle: string) => {
    setExpandedPlans((prev) => ({ ...prev, [planTitle]: !prev[planTitle] }));
  };

  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ py: 8 }}>
        {checkoutCancelled ? (
          <Alert severity="info" sx={{ maxWidth: 560, mx: 'auto', mb: 3 }} onClose={() => setDismissCancelledBanner(true)}>
            Checkout was cancelled. You can start a Pro trial anytime from this page.
          </Alert>
        ) : null}
        <Typography align="center" variant="h3" fontWeight={700} mb={3} color="text.primary">
          Choose Your FloAI Studio Plan
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={isAnnual}
            exclusive
            onChange={handlePricingToggle}
            aria-label="pricing period"
          >
            <ToggleButton value={false} aria-label="monthly">
              Monthly
            </ToggleButton>
            <ToggleButton value={true} aria-label="annually">
              Annually <Chip label="Save 20%" color="secondary" size="small" sx={{ ml: 1 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Grid container justifyContent="center" alignItems="stretch" spacing={4}>
          {PLANS.map((plan, /* _ */) => (
            <Grid key={plan.title} item xs={12} sm={6} md={4} sx={{ display: 'flex', flexGrow: 1, maxWidth: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, width: '100%', height: '100%', mx: 'auto', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={700} variant="subtitle1" mb={0.5} color="text.primary">
                    {plan.title}
                    {plan.highlight && <Chip label="Most Popular" size="small" color="secondary" sx={{ ml: 1 }} />}
                    {plan.recommended && <Chip label="Recommended" size="small" color="info" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="h4" mb={2} color="text.primary">{isAnnual && plan.annualPrice ? plan.annualPrice : plan.price}</Typography>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {plan.features.map((f) => (
                      <li key={f} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.1rem' }} /><Typography fontSize={14} color="text.secondary">{f}</Typography></li>
                    ))}
                  </ul>
                </CardContent>
                <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  {plan.title === 'Pro' ? (
                    <ProCheckoutButton
                      fullWidth
                      billing={isAnnual ? 'annual' : 'monthly'}
                      variant="contained"
                      color={plan.color as 'secondary'}
                      sx={{
                        mt: 1,
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': { transform: 'translateY(-2px)' },
                      }}
                    >
                      {plan.cta}
                    </ProCheckoutButton>
                  ) : plan.title === 'Free' ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      color={plan.color as 'primary'}
                      component={Link}
                      href="/auth"
                      sx={{
                        mt: 1,
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': { transform: 'translateY(-2px)' },
                      }}
                    >
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      color={plan.color as 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                      sx={{
                        mt: 1,
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': { transform: 'translateY(-2px)' },
                      }}
                    >
                      {plan.cta}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        </Box>

      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Typography align="center" variant="h3" fontWeight={700} mb={6} color="text.primary">
          Compare FloAI Studio Plans
        </Typography>

        {/* Mobile: card per plan with all comparison data (no horizontal scroll), expandable */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Grid container spacing={3}>
            {PLANS.map((plan) => {
              const planKey = PLAN_TO_FEATURE_KEY[plan.title];
              if (!planKey) return null;
              const isExpanded = !!expandedPlans[plan.title];
              const visibleFeatures = isExpanded
                ? FEATURES_COMPARISON
                : FEATURES_COMPARISON.slice(0, FEATURES_VISIBLE_COLLAPSED);
              const hiddenCount = FEATURES_COMPARISON.length - FEATURES_VISIBLE_COLLAPSED;
              return (
                <Grid item xs={12} key={plan.title}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: 'visible' }}>
                    <CardContent sx={{ pb: 0 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography fontWeight={700} variant="h6" color="text.primary">
                          {plan.title}
                        </Typography>
                        {plan.highlight && <Chip label="Most Popular" size="small" color="secondary" />}
                        {plan.recommended && <Chip label="Recommended" size="small" color="info" />}
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {isAnnual && plan.annualPrice ? plan.annualPrice : plan.price}
                      </Typography>
                      <Box component="dl" sx={{ m: 0 }}>
                        {visibleFeatures.map((feature, index) => {
                          const value = feature[planKey];
                          const { icon, color } = getFeatureIcon(feature.name);
                          return (
                            <Box
                              key={feature.name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                py: 1.25,
                                borderBottom: index < visibleFeatures.length - 1 ? 1 : 0,
                                borderColor: 'divider',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                                {icon && <Tooltip title={feature.name} arrow>{icon}</Tooltip>}
                                <Typography component="dt" variant="body2" fontWeight={600} sx={{ color }} noWrap>
                                  {feature.name}
                                </Typography>
                              </Box>
                              <Box component="dd" sx={{ m: 0, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                {typeof value === 'string' ? (
                                  <Typography variant="body2" color="text.secondary">{value}</Typography>
                                ) : (
                                  value
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      {FEATURES_COMPARISON.length > FEATURES_VISIBLE_COLLAPSED && (
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => togglePlanExpanded(plan.title)}
                          endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMore />}
                          sx={{ mt: 1, mb: 0 }}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded
                            ? 'Show less'
                            : `Show ${hiddenCount} more features`}
                        </Button>
                      )}
                    </CardContent>
                    <CardActions sx={{ pt: 0, px: 2, pb: 2, flexDirection: 'column', alignItems: 'stretch' }}>
                      {plan.title === 'Pro' ? (
                        <ProCheckoutButton
                          fullWidth
                          billing={isAnnual ? 'annual' : 'monthly'}
                          variant="contained"
                          color={plan.color as 'secondary'}
                        >
                          {plan.cta}
                        </ProCheckoutButton>
                      ) : plan.title === 'Free' ? (
                        <Button fullWidth variant="outlined" component={Link} href="/auth" color={plan.color as 'primary'}>
                          {plan.cta}
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          color={plan.color as 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                        >
                          {plan.cta}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Desktop: full comparison table */}
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
          <Table stickyHeader aria-label="pricing comparison table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Features</TableCell>
                {PLANS.map((plan) => (
                  <TableCell key={plan.title} align="center" sx={{ fontWeight: 'bold' }}>
                    <Box sx={{ transition: 'transform 0.2s ease-in-out', display: 'inline-block',
                      '&:hover': { transform: 'translateY(-2px)' } }}>
                      {plan.title} {isAnnual && plan.annualPrice ? `(${plan.annualPrice})` : `(${plan.price})`}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {FEATURES_COMPARISON.map((feature, index) => {
                const { icon, color } = getFeatureIcon(feature.name);
                return (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 'bold', color, display: 'flex', alignItems: 'center', height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: '60px', height: '100%' }}>
                        {icon && <Tooltip title={feature.name} arrow>{icon}</Tooltip>}
                        {feature.name}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'free' in feature ? feature.free : <CancelOutlined color="error" />}</TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'creator' in feature ? feature.creator : <CancelOutlined color="error" />}</TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'pro' in feature ? feature.pro : <CancelOutlined color="error" />}</TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'enterprise' in feature ? feature.enterprise : <CancelOutlined color="error" />}</TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'whiteLabelStandard' in feature ? feature.whiteLabelStandard : <CancelOutlined color="error" />}</TableCell>
                    <TableCell align="center" sx={{ minHeight: '60px' }}>{'whiteLabelEnterprise' in feature ? feature.whiteLabelEnterprise : <CancelOutlined color="error" />}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Enterprise/White-Label Direct Contact */}
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography align="center" variant="h3" fontWeight={700} mb={3} color="text.primary">
          Need a Custom Solution?
        </Typography>
        <Typography variant="h6" color="text.secondary" mb={4}>
          Our Enterprise and White-Label plans are tailored to your specific needs.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          href="/contact" // Link to the contact page
          sx={marketingPrimaryCtaSx({
            py: 1.5,
            px: 4,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-2px)', boxShadow: 4 },
          })}
        >
          Contact Sales
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Or email us directly at{' '}
          <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
            {SUPPORT_EMAIL}
          </MuiLink>
        </Typography>
      </Box>
      </Container>
    </PublicPageLayout>
  );
}

// Helper function to get feature icon and color
const getFeatureIcon = (featureName: string) => {
  switch (featureName) {
    case 'Social Accounts': return { icon: <People color="info" />, color: 'info.main' };
    case 'Analytics': return { icon: <AnalyticsOutlined color="warning" />, color: 'warning.main' };
    case 'Scheduling': return { icon: <ScheduleOutlined color="secondary" />, color: 'secondary.main' };
    case 'Academy Courses': return { icon: <SchoolOutlined color="success" />, color: 'success.main' };
    case 'Template Marketplace': return { icon: <StorefrontOutlined color="primary" />, color: 'primary.main' };
    case 'Support': return { icon: <SupportAgentOutlined color="success" />, color: 'success.main' };
    case 'Early Access to Features': return { icon: <StarsOutlined color="action" />, color: 'action.active' };
    case 'White-Label Options': return { icon: <BusinessOutlined color="info" />, color: 'info.main' };
    case 'Custom Integrations': return { icon: <IntegrationInstructionsOutlined color="secondary" />, color: 'secondary.main' };
    case 'SLA Guarantees': return { icon: <VerifiedUserOutlined color="success" />, color: 'success.main' };
    case 'API Access': return { icon: <ApiOutlined color="primary" />, color: 'primary.main' };
    default: return { icon: null, color: 'text.primary' };
  }
};

export default function PricingPage() {
  return (
    <Suspense fallback={<Box sx={{ py: 8, textAlign: 'center' }}><Typography color="text.secondary">Loading pricing…</Typography></Box>}>
      <PricingPageInner />
    </Suspense>
  );
}
